import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDocs,
  increment,
  onSnapshot,
  setDoc,
  updateDoc,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FoodItem, MenuOption } from '../../types'
import { normalizeStoredOptions } from '@shared/menuImport/optionNormalize'
import { dispatchCatalogEvent, MENU_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import {
  assignPerCategorySortIndices,
  sortMenuItemsByCategoryOrder,
} from '../menuSortOrder'
import { getCategories, UNCATEGORIZED_CATEGORY_ID } from './categories'
import { effectiveMenuCategoryId } from '../menuItemReorder'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'
import {
  POS_EXTRA_ITEM_ID,
  applyPosExtraLock,
  catalogExtraNeedsRelock,
  defaultPosExtraItemFields,
  extraUncategorizedSortIndex,
  isPosExtraItem,
  lockPosExtraFields,
  mergePosExtraIntoItems,
} from '@shared/lib/posExtraItem'
import {
  applyStockConsumeToItem,
  mergeSoldOutFromPrevious,
  soldOutStatesEqual,
  withNormalizedSoldOut,
  type SoldOutChoice,
} from '@shared/lib/soldOutItem'

function normalizeOption(opt: MenuOption): MenuOption {
  const choices = opt.choices ?? []
  const prices = opt.choicePrices ?? []
  const choicePrices = choices.map((_, i) => (Array.isArray(prices) && Number.isFinite(prices[i]) ? prices[i]! : 0))
  return { ...opt, choices, choicePrices }
}

function normalizeOptions(
  opts: FoodItem['options'],
  itemId: string,
): MenuOption[] {
  return normalizeStoredOptions(opts, itemId).map(normalizeOption)
}

function normalizeItem(item: FoodItem & { restaurantId?: string }): FoodItem {
  const { restaurantId: _dropped, ...rest } = item
  const options = normalizeOptions(rest.options, rest.id)
  const sortIndex =
    typeof rest.sortIndex === 'number' && Number.isFinite(rest.sortIndex)
      ? rest.sortIndex
      : undefined
  return withNormalizedSoldOut({
    ...rest,
    options,
    sortIndex,
    ...(rest.posOnly === true ? { posOnly: true } : {}),
  })
}

const menuItemsCacheByPlace: Record<string, FoodItem[]> = {}
let menuItemsUnsubscribe: (() => void) | null = null

const MENU_FIRESTORE_BACKUP_KEY = 'wortelogy-menu-firestore-backup'
const MENU_MAIN_STORAGE_KEY = 'wortelogy-menu'

function saveBackup(placeId: string, items: FoodItem[]): void {
  if (typeof localStorage !== 'undefined') {
    try {
      const json = JSON.stringify(items)
      localStorage.setItem(`${MENU_FIRESTORE_BACKUP_KEY}-${placeId}`, json)
      localStorage.setItem(`${MENU_MAIN_STORAGE_KEY}-${placeId}`, json)
    } catch {
      // ignore quota or other errors
    }
  }
}

function loadBackup(placeId: string): FoodItem[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(`${MENU_FIRESTORE_BACKUP_KEY}-${placeId}`)
    if (!raw) return []
    const parsed = JSON.parse(raw) as FoodItem[]
    return Array.isArray(parsed) ? parsed.map(normalizeItem) : []
  } catch {
    return []
  }
}

function menuItemsRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'menuItems')
}

function menuItemDoc(placeId: string, itemId: string) {
  return doc(getFirestoreInstance(), 'places', placeId, 'menuItems', itemId)
}

/** Firestore does not allow undefined; convert to null so setDoc succeeds. */
function toFirestoreData(item: FoodItem): Record<string, unknown> {
  function undefinedToNull(value: unknown): unknown {
    if (value === undefined) return null
    if (Array.isArray(value)) return value.map(undefinedToNull)
    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, undefinedToNull(v)]),
      )
    }
    return value
  }
  return undefinedToNull(item) as Record<string, unknown>
}

function itemsFromSnapshotDocs(docs: QueryDocumentSnapshot[]): FoodItem[] {
  return docs.map((d) =>
    normalizeItem({ id: d.id, ...d.data() } as FoodItem),
  )
}

function applyMenuItemsCache(placeId: string, items: FoodItem[], backup: boolean): void {
  menuItemsCacheByPlace[placeId] = items
  if (backup && items.length > 0) saveBackup(placeId, items)
}

/** Merge Extra into the live cache so a snapshot without pos-extra cannot wipe it. */
function ingestMenuItems(
  placeId: string,
  items: FoodItem[],
  backup: boolean,
): { hadExtra: boolean; needsPersist: boolean } {
  const existing = items.find(isPosExtraItem)
  const hadExtra = existing != null
  const needsPersist = !hadExtra || catalogExtraNeedsRelock(existing)
  const merged = mergePosExtraIntoItems(items, (sortIndex) =>
    buildDefaultPosExtraItem(items, sortIndex),
  ).map(applyPosExtraLock)
  const ordered = sortMenuItemsByCategoryOrder(merged, getCategories())
  applyMenuItemsCache(placeId, ordered, backup)
  return { hadExtra, needsPersist }
}

function dispatchMenuUpdated(): void {
  dispatchCatalogEvent(MENU_UPDATED_EVENT)
}

/** Re-sort cached items after categories change (called from categories snapshot). */
export function resortMenuItemsCache(): void {
  const placeId = getCurrentPlaceId()
  const list = menuItemsCacheByPlace[placeId]
  if (!list) return
  menuItemsCacheByPlace[placeId] = sortMenuItemsByCategoryOrder(list, getCategories())
}

export async function primeMenuItemsCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (menuItemsUnsubscribe) {
    menuItemsUnsubscribe()
    menuItemsUnsubscribe = null
  }
  let primeNeedsPersist = true
  try {
    const snap = await getDocs(menuItemsRef(placeId))
    const items = itemsFromSnapshotDocs(snap.docs)
    primeNeedsPersist = ingestMenuItems(placeId, items, items.length > 0).needsPersist
  } catch (e) {
    console.error('Firestore primeMenuItemsCache failed', e)
  }
  if (!(placeId in menuItemsCacheByPlace) || menuItemsCacheByPlace[placeId].length === 0) {
    const backup = loadBackup(placeId)
    if (backup.length > 0) {
      primeNeedsPersist = ingestMenuItems(placeId, backup, false).needsPersist
    } else {
      try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(`${MENU_MAIN_STORAGE_KEY}-${placeId}`) : null
        if (raw) {
          const parsed = JSON.parse(raw) as FoodItem[]
          if (Array.isArray(parsed) && parsed.length > 0) {
            primeNeedsPersist = ingestMenuItems(placeId, parsed.map(normalizeItem), false).needsPersist
          } else {
            primeNeedsPersist = ingestMenuItems(placeId, [], false).needsPersist
          }
        } else {
          primeNeedsPersist = ingestMenuItems(placeId, [], false).needsPersist
        }
      } catch {
        primeNeedsPersist = ingestMenuItems(placeId, [], false).needsPersist
      }
    }
  }
  menuItemsUnsubscribe = onSnapshot(menuItemsRef(placeId), (snapshot) => {
    const items = itemsFromSnapshotDocs(snapshot.docs)
    const { needsPersist } = ingestMenuItems(placeId, items, items.length > 0)
    dispatchMenuUpdated()
    if (needsPersist) void persistPosExtraDoc()
  })
  if (primeNeedsPersist) void persistPosExtraDoc()
}

export function getFoodItems(): FoodItem[] {
  const placeId = getCurrentPlaceId()
  return menuItemsCacheByPlace[placeId] ?? []
}

export function saveFoodItems(items: FoodItem[]): void {
  const placeId = getCurrentPlaceId()
  const previous = menuItemsCacheByPlace[placeId] ?? []
  const prevById = new Map(previous.map((i) => [i.id, i]))
  const merged = items.map((item) =>
    mergeSoldOutFromPrevious(normalizeItem(item), prevById.get(item.id)),
  )
  const normalized = mergeExistingExtra(merged).map(applyPosExtraLock)
  const ordered = sortMenuItemsByCategoryOrder(normalized, getCategories())
  const withSort = assignPerCategorySortIndices(ordered)
  applyMenuItemsCache(placeId, withSort, true)
  dispatchMenuUpdated()
  ;(async () => {
    const snap = await getDocs(menuItemsRef(placeId))
    const ids = new Set(withSort.map((i) => i.id))
    const ops: BatchWriteOp[] = []
    for (const d of snap.docs) {
      const data = d.data() as FoodItem
      if (ids.has(d.id) || isPosExtraItem({ id: d.id, menuKey: data.menuKey })) {
        continue
      }
      ops.push({ type: 'delete', ref: d.ref })
    }
    for (const item of menuItemsCacheByPlace[placeId]) {
      ops.push({ type: 'set', ref: menuItemDoc(placeId, item.id), data: toFirestoreData(item) })
    }
    await commitWriteOps(ops)
  })().catch((e) => console.error('Firestore saveFoodItems failed', e))
}

function generateFoodId(): string {
  return `F-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function buildDefaultPosExtraItem(cache: FoodItem[], sortIndex?: number): FoodItem {
  return normalizeItem({
    id: POS_EXTRA_ITEM_ID,
    ...defaultPosExtraItemFields(),
    sortIndex: sortIndex ?? extraUncategorizedSortIndex(cache),
  })
}

function mergeExistingExtra(items: FoodItem[]): FoodItem[] {
  return mergePosExtraIntoItems(items, (sortIndex) =>
    buildDefaultPosExtraItem(items, sortIndex),
  )
}

let persistExtraInFlight = false
let persistExtraQueued = false

/** Write pos-extra when Firestore is missing it. Queues one retry if a write is already in flight. */
async function persistPosExtraDoc(): Promise<void> {
  if (persistExtraInFlight) {
    persistExtraQueued = true
    return
  }
  persistExtraInFlight = true
  try {
    do {
      persistExtraQueued = false
      const placeId = getCurrentPlaceId()
      const extra = (menuItemsCacheByPlace[placeId] ?? []).find(isPosExtraItem)
      if (!extra) continue
      await setDoc(
        menuItemDoc(placeId, POS_EXTRA_ITEM_ID),
        toFirestoreData(normalizeItem(applyPosExtraLock(extra))),
      )
    } while (persistExtraQueued)
  } catch (e) {
    console.error('Firestore ensurePosExtraItem failed', e)
  } finally {
    persistExtraInFlight = false
    if (persistExtraQueued) {
      persistExtraQueued = false
      void persistPosExtraDoc()
    }
  }
}

/** Create the built-in Extra item if this place has none. Idempotent (stable Firestore id). */
export async function ensurePosExtraItem(): Promise<void> {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const hadExtra = cache.some(isPosExtraItem)
  if (hadExtra) return
  ingestMenuItems(placeId, cache, cache.length > 0)
  dispatchMenuUpdated()
  await persistPosExtraDoc()
}

/** Adds item to cache and persists to Firestore. Returns the created item after write completes. */
export async function addFoodItem(item: Omit<FoodItem, 'id'>): Promise<FoodItem> {
  const placeId = getCurrentPlaceId()
  const id = generateFoodId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const catKey = item.categoryId ?? UNCATEGORIZED_CATEGORY_ID
  const inCat = cache.filter(
    (i) => (i.categoryId ?? UNCATEGORIZED_CATEGORY_ID) === catKey,
  )
  const maxIdx = inCat.reduce((m, i) => Math.max(m, i.sortIndex ?? -1), -1)
  const full: FoodItem = normalizeItem({
    ...item,
    id,
    sortIndex: maxIdx + 1,
  })
  menuItemsCacheByPlace[placeId] = [...cache, full]
  saveBackup(placeId, menuItemsCacheByPlace[placeId])
  dispatchMenuUpdated()
  await setDoc(menuItemDoc(placeId, id), toFirestoreData(full))
  return full
}

export function updateFoodItem(
  id: string,
  patch: Partial<FoodItem>,
): FoodItem[] {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const current = cache.find((i) => i.id === id)
  const lockedPatch =
    current && isPosExtraItem(current)
      ? {
          ...patch,
          ...lockPosExtraFields(),
          categoryId: undefined,
          station: undefined,
        }
      : patch
  menuItemsCacheByPlace[placeId] = cache.map((i) =>
    i.id === id
      ? applyPosExtraLock(normalizeItem({ ...i, ...lockedPatch }))
      : i,
  )
  const found = menuItemsCacheByPlace[placeId].find((i) => i.id === id)
  if (found) {
    setDoc(menuItemDoc(placeId, id), toFirestoreData(found)).catch((e) =>
      console.error('Firestore updateFoodItem failed', e),
    )
  }
  saveBackup(placeId, menuItemsCacheByPlace[placeId])
  dispatchMenuUpdated()
  return menuItemsCacheByPlace[placeId]
}

export type SoldOutItemState = {
  soldOut: boolean
  soldOutChoices?: SoldOutChoice[]
  stockRemaining?: number
  soldOutReason?: 'manual' | 'depleted'
}

/** Replace per-item 86 + stock overlay. Writes only documents whose overlay changed. */
export async function setFoodItemsSoldOut(
  states: ReadonlyMap<string, SoldOutItemState>,
): Promise<FoodItem[]> {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const next = cache.map((item) => {
    if (isPosExtraItem(item)) return applyPosExtraLock(normalizeItem(item))
    const state = states.get(item.id)
    return normalizeItem({
      ...item,
      soldOut: state?.soldOut === true,
      soldOutChoices: state?.soldOutChoices ?? [],
      stockRemaining: state?.stockRemaining,
      soldOutReason: state?.soldOutReason,
    })
  })
  menuItemsCacheByPlace[placeId] = next
  saveBackup(placeId, next)
  dispatchMenuUpdated()
  const ops: BatchWriteOp[] = []
  for (const item of next) {
    const prev = cache.find((c) => c.id === item.id)
    if (prev && soldOutStatesEqual(prev, item)) continue
    ops.push({ type: 'set', ref: menuItemDoc(placeId, item.id), data: toFirestoreData(item) })
  }
  if (ops.length > 0) {
    await commitWriteOps(ops)
  }
  return next
}

/**
 * Fire-and-forget remaining increment for tracked items only.
 * Does not join order/bill writes. Unlimited items are skipped.
 */
export function applyItemStockDeltas(deltas: ReadonlyMap<string, number>): void {
  if (deltas.size === 0) return
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  let changed = false
  const next = cache.map((item) => {
    const consume = deltas.get(item.id) ?? 0
    if (consume === 0 || typeof item.stockRemaining !== 'number') return item
    changed = true
    return applyPosExtraLock(normalizeItem(applyStockConsumeToItem(item, consume)))
  })
  if (!changed) return
  menuItemsCacheByPlace[placeId] = next
  saveBackup(placeId, next)
  dispatchMenuUpdated()
  void (async () => {
    for (const item of next) {
      const consume = deltas.get(item.id) ?? 0
      if (consume === 0) continue
      const prev = cache.find((c) => c.id === item.id)
      if (!prev || typeof prev.stockRemaining !== 'number') continue
      const ref = menuItemDoc(placeId, item.id)
      try {
        await updateDoc(ref, { stockRemaining: increment(-consume) })
        if (
          (prev.soldOut === true) !== (item.soldOut === true) ||
          (prev.soldOutReason ?? undefined) !== (item.soldOutReason ?? undefined) ||
          item.stockRemaining === 0
        ) {
          await updateDoc(ref, {
            ...(item.soldOut === true ? { soldOut: true } : { soldOut: deleteField() }),
            ...(item.soldOutReason
              ? { soldOutReason: item.soldOutReason }
              : { soldOutReason: deleteField() }),
            ...(item.stockRemaining === 0 ? { stockRemaining: 0 } : {}),
          })
        }
      } catch (e) {
        console.error('Firestore applyItemStockDeltas failed', e)
      }
    }
  })()
}

export function deleteFoodItem(id: string): FoodItem[] {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const target = cache.find((i) => i.id === id)
  if (target && isPosExtraItem(target)) return cache
  menuItemsCacheByPlace[placeId] = cache.filter((i) => i.id !== id)
  deleteDoc(menuItemDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteFoodItem failed', e),
  )
  saveBackup(placeId, menuItemsCacheByPlace[placeId])
  dispatchMenuUpdated()
  return menuItemsCacheByPlace[placeId]
}

export function clearCategoryFromItems(categoryId: string): void {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const updated = cache.map((i) =>
    i.categoryId === categoryId ? { ...i, categoryId: undefined } : i,
  )
  menuItemsCacheByPlace[placeId] = updated
  saveFoodItems(updated)
}

export function clearStationFromItems(stationName: string): void {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const updated = cache.map((i) =>
    i.station === stationName ? { ...i, station: undefined } : i,
  )
  menuItemsCacheByPlace[placeId] = updated
  saveFoodItems(updated)
}

/** Set station on all items in a category (effective category id, including uncategorized bucket). */
export function applyCategoryStationToItems(
  categoryId: string,
  station?: string,
): void {
  const categories = getCategories()
  const validCategoryIds = new Set(categories.map((c) => c.id))
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const trimmed = station?.trim()
  const updated = cache.map((i) => {
    if (isPosExtraItem(i)) return i
    if (effectiveMenuCategoryId(i, validCategoryIds) !== categoryId) return i
    if (trimmed) return { ...i, station: trimmed }
    const { station: _s, ...rest } = i
    return rest as FoodItem
  })
  saveFoodItems(updated)
}

export function replaceStationNameInItems(
  oldName: string,
  newName: string,
): void {
  const placeId = getCurrentPlaceId()
  const cache = menuItemsCacheByPlace[placeId] ?? []
  const updated = cache.map((i) =>
    i.station === oldName ? { ...i, station: newName } : i,
  )
  menuItemsCacheByPlace[placeId] = updated
  saveFoodItems(updated)
}

export function isMenuItemsPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in menuItemsCacheByPlace
}

/** Clear in-memory cache and unsubscribe from real-time listener. */
export function clearMenuItemsCache(): void {
  if (menuItemsUnsubscribe) {
    menuItemsUnsubscribe()
    menuItemsUnsubscribe = null
  }
  Object.keys(menuItemsCacheByPlace).forEach((k) => delete menuItemsCacheByPlace[k])
}
